import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, FlatList, BackHandler } from 'react-native';
import Spinner from '@Frontend_Ui_Lib_App/Spinner';

import TrainCard from './TrainCard';
import { SeparatorWithTitle } from './AlternateTrainListingCard';
import { Actions } from '../../navigation';
import { connect } from 'react-redux';
import { parse, format } from 'fecha';
import {  colors } from '@mmt/legacy-commons/Styles/globalStyles';
import { initBookBreakJourney, clearSelectedBJTrainInfo, bookBreakJourneyClicked } from '../NewListing/RailsListingActions';
import RailsHeader from '../NewListing/Containers/RailsHeaderContainer';
import {fontStyle, getLineHeight} from "../../vernacular/VernacularUtils";

interface BookBreakJourneyProps {
  isBreakJourneyLoading: boolean;
  loadingMsg: string;
  breakJourneyId: string;
  clearSelectedBJTrainInfo: () => void;
}

function BookBreakJourney (props: BookBreakJourneyProps) {
  const {isBreakJourneyLoading: isLoadingFromRedux, loadingMsg} = props;
  const [showLoader, setShowLoader] = useState(false);

  const onHardBackPress = () => {
    props.clearSelectedBJTrainInfo();
    Actions.pop();
    return true;
  };

  useEffect(() => {
    BackHandler.addEventListener('hardwareBackPress', onHardBackPress);
    return () => {
      BackHandler.removeEventListener('hardwareBackPress', onHardBackPress);
    };
  }, []);

  const hideLoader = () => {
    setShowLoader(false);
  };

  const onBookBreakJourneyClicked = (item) => () => {
    setShowLoader(true);
    props.bookBreakJourneyClicked(item.item, props.breakJourneyId, hideLoader)
  };

  if(isLoadingFromRedux) {
    return (
      <View style={{ flex: 1 }}>
        <RailsHeader
          onBack={onHardBackPress}
          page="newListing"
          showEditIcon={false}
        />
        <View style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
          <Spinner size={30} color={colors.azure} />
          {Boolean(loadingMsg) && <Text style={[styles.loadingMessage, fontStyle("bold"), getLineHeight(14)]}>{loadingMsg}</Text>}
        </View>
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <View>
        <RailsHeader
          onBack={onHardBackPress}
          page="newListing"
          showEditIcon={false}
        />
      </View>
      {showLoader &&
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Spinner size={30} color={colors.azure} />
        </View>
      }
      {!showLoader && Array.isArray(props?.selectedBreakJourney?.segmentList) &&
        <FlatList
          data={props?.selectedBreakJourney?.segmentList?.map(convertBJTrainInfoToNormalTrainInfo)}
          keyExtractor={item => item.segmentIndex}
          renderItem={(item, index) => (
            <TrainCard
              {...item}
              header={`TRAIN ${(index + 1)}`}
              breakJourneyId={props.breakJourneyId}
              bookBreakJourneyClicked={onBookBreakJourneyClicked(item)}
            />
          )}
          ItemSeparatorComponent={(prop) => {
            return (
              <View style={{paddingHorizontal: 16}}>
                <SeparatorWithTitle title={`Next train after ${props.selectedBreakJourney.layoverDurationText}`} />
              </View>
            );
          }}
        />
      }
    </View>
  );
};

export const convertBJTrainInfoToNormalTrainInfo = (BJTrain) => {
  const departureDateAndTime = parse(BJTrain.departureTime, 'YYYY-MM-DDTHH:mm:ss');
  const arrivalDateAndTime = parse(BJTrain.arrivalTime, 'YYYY-MM-DDTHH:mm:ss');
  const clusterTrain = BJTrain.clusterTrain || false;
  const departureTime = format(departureDateAndTime, 'HH:mm');
  const arrivalTime = format(arrivalDateAndTime, 'HH:mm');
  const duration = BJTrain.durationInMins;
  const frmStnCode = BJTrain.sourceStationCode;
  const frmStnName = BJTrain.sourceStationName;
  const toStnCode = BJTrain.destinationStationCode;
  const toStnName = BJTrain.destinationStationName;
  const trainName = BJTrain.trainName;
  const trainNumber = BJTrain.trainNumber;
  const segmentIndex = BJTrain.segmentIndex;
  const {
    seatsAvailability,
    availabilityType,
    prettyPrintingAvailabilityStatus,
    journeyClass,
    journeyQuota,
    lastUpdatedOn,
    dynamicFareFlag,
    segmentFare
  } = BJTrain;

  return {
    segmentIndex,
    seatsAvailability,
    availabilityType,
    prettyPrintingAvailabilityStatus,
    journeyClass,
    journeyQuota,
    totalFare: segmentFare,
    trainInfo: {
      departureDateAndTime,
      arrivalDateAndTime,
      clusterTrain,
      departureTime,
      arrivalTime,
      duration,
      frmStnCode,
      frmStnName,
      toStnCode,
      toStnName,
      trainName,
      trainNumber,
      lastUpdatedOn,
      dynamicFareFlag,
    }
  };
};

const styles = StyleSheet.create({
  separatorStyle: {
    height: 0.5,
    borderColor: '#e7e7e7',
    borderWidth: 0.5,
    borderStyle: 'dashed',
    borderRadius: 1,
    marginBottom: 15
  },
  loadingMessage: {
    fontSize: 14,
    color: colors.defaultTextColor,
    marginTop: 30
  },
  layoverStyle: {
    fontSize: 12,
    color: '#4a4a4a'
  },
  layoverTextContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#e7e7e7',
    borderRadius: 8.5,
    width: 174,
    position: 'absolute',
    top: -7
  },
  layoverContainer: {
    display: 'flex',
    alignItems: 'center',
    marginHorizontal: 16
  }
});

const mapStateToProps = (state, ownProps) => {
  const {
    railsListing: {
      selectedBreakJourney,
      bookBreakJourneyViewState,
      isBreakJourneyLoading,
      loadingMsg
    }
  } = state;
  const breakJourneyId = selectedBreakJourney?.breakJourneyId;
  return {
    ...ownProps,
    selectedBreakJourney,
    bookBreakJourneyViewState,
    breakJourneyId,
    isBreakJourneyLoading,
    loadingMsg
  };
};

const mapDispatchToProps = dispatch => ({
  initBookBreakJourney: (breakJourneyRequest) => {
    dispatch(initBookBreakJourney(breakJourneyRequest));
  },
  clearSelectedBJTrainInfo: () => {
    dispatch(clearSelectedBJTrainInfo());
  },
  bookBreakJourneyClicked: (data, breakJourneyId, hideLoader) => {
    dispatch(bookBreakJourneyClicked(data, breakJourneyId, hideLoader));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(BookBreakJourney);
